/**
 * Metafans BuddyPress Media Viewer
 * Handles the lightbox, image navigation, and media interactions.
 */

import { postAction } from './api.js';

class MediaViewer {
    constructor() {
        this.container = document.querySelector('.th-media-viewer-container');
        this.body = document.body;

        // Bind methods to maintain 'this' context
        this.openViewer = this.openViewer.bind(this);
        this.closeViewer = this.closeViewer.bind(this);
        this.handleNavigation = this.handleNavigation.bind(this);
        this.handleKeyboard = this.handleKeyboard.bind(this);

        this.init();
    }

    init() {
        if (!this.container) return;

        // Event Delegation
        this.bindEvents();
    }

    bindEvents() {
        // Open viewer on clicking a thumbnail
        document.addEventListener('click', (e) => {
            const thumb = e.target.closest('.media-popup-thumbnail');
            if (thumb) {
                e.preventDefault();
                this.openViewer(thumb);
            }
        });

        // Close viewer
        this.container.querySelector('.close')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.closeViewer();
        });

        // Navigation (Next/Prev)
        const navContainer = this.container.querySelector('.image-viewer-next-prev');
        if (navContainer) {
            navContainer.addEventListener('click', this.handleNavigation);
        }

        // Keyboard navigation (Escape, Left, Right)
        document.addEventListener('keyup', this.handleKeyboard);
    }

    /**
     * Opens the media viewer for a specific element
     * @param {HTMLElement} element - The .media-popup-thumbnail element
     */
    openViewer(element) {
        const wrapper = element.closest('.bp-image-single');
        if (!wrapper) return;

        const currentId = wrapper.id;
        const fullUrl = element.getAttribute('href');
        const mediaId = element.dataset.id;
        const activityId = element.dataset.activity;

        this.updateCurrentId(currentId);
        this.container.classList.add('show');
        this.renderMedia(fullUrl);

        // Set data attributes for context
        this.container.setAttribute('data-media-id', mediaId);
        this.container.setAttribute('data-activity-id', activityId);

        // Fetch async data
        this.fetchAuthor(mediaId, activityId);
        this.fetchComments(mediaId, activityId);
    }

    closeViewer() {
        this.container.classList.remove('show');
        // Clear media to stop video playback
        const viewContainer = this.container.querySelector('.th-media-view');
        if (viewContainer) viewContainer.innerHTML = '';
    }

    handleNavigation(e) {
        const btn = e.target.closest('span');
        if (!btn) return;

        const type = btn.classList.contains('img-next') ? 'next' : 'prev';
        const currentId = btn.dataset.currentId; // This is set in the DOM by updateCurrentId

        // Find adjacent ID
        const nextId = type === 'next'
            ? this.getNextImageId(currentId)
            : this.getPrevImageId(currentId);

        const nextWrapper = document.querySelector(`.bp-image-single#${CSS.escape(nextId)}`);

        if (nextWrapper) {
            const thumb = nextWrapper.querySelector('.media-popup-thumbnail');
            if (thumb) {
                const mediaId = thumb.dataset.id;
                const activityId = thumb.dataset.activity;
                const url = thumb.getAttribute('href');

                this.updateCurrentId(nextId);
                this.renderMedia(url);
                this.fetchAuthor(mediaId, activityId);
                this.fetchComments(mediaId, activityId);

                // Update container context
                this.container.setAttribute('data-media-id', mediaId);
                this.container.setAttribute('data-activity-id', activityId);
            }
        }
    }

    handleKeyboard(e) {
        if (!this.container.classList.contains('show')) return;

        switch (e.key) {
            case 'Escape':
                this.closeViewer();
                break;
            case 'ArrowLeft':
                this.triggerNavClick('.img-prev');
                break;
            case 'ArrowRight':
                this.triggerNavClick('.img-next');
                break;
        }
    }

    triggerNavClick(selector) {
        const btn = this.container.querySelector(`.image-viewer-next-prev ${selector}`);
        if (btn) btn.click();
    }

    /**
     * Logic to determine the next image ID based on DOM order
     */
    getNextImageId(currentId) {
        // Logic assumes IDs are numeric or sequential in the DOM logic from original code.
        // Original code logic: generic increment based on total count, or finding by ID.
        // Refactored to be safer:
        const allImages = Array.from(document.querySelectorAll('.bp-image-single'));
        const currentIndex = allImages.findIndex(el => el.id == currentId);

        if (currentIndex === -1) return allImages[0]?.id; // Fallback

        const nextIndex = currentIndex + 1;
        return nextIndex < allImages.length ? allImages[nextIndex].id : allImages[allImages.length - 1].id;
    }

    getPrevImageId(currentId) {
        const allImages = Array.from(document.querySelectorAll('.bp-image-single'));
        const currentIndex = allImages.findIndex(el => el.id == currentId);

        if (currentIndex === -1) return allImages[0]?.id;

        const prevIndex = currentIndex - 1;
        return prevIndex >= 0 ? allImages[prevIndex].id : allImages[0].id;
    }

    updateCurrentId(id) {
        const navSpans = this.container.querySelectorAll('.image-viewer-next-prev span');
        navSpans.forEach(span => span.setAttribute('data-current-id', id));
    }

    renderMedia(url) {
        if (!url) return;
        const viewContainer = this.container.querySelector('.th-media-view');
        const ext = url.split('.').pop().toLowerCase();
        const videoExtensions = ['mp4', 'webm', 'ogg', 'mov'];
        let html = '';

        if (videoExtensions.includes(ext)) {
            html = `<video controls autoplay playsinline src="${url}" style="max-width: 100%; max-height: 80vh;"></video>`;
        } else {
            html = `<img src="${url}" alt="Media Preview" style="max-width: 100%; max-height: 80vh;" />`;
        }

        viewContainer.innerHTML = html;
    }

    async fetchAuthor(media_id, activity_id) {
        const authorSection = this.container.querySelector('.th-media-comments .author_section');
        if (!authorSection) return;

        // clear previous
        authorSection.innerHTML = '';

        try {
            const response = await postAction('th_bp_media_author', { media_id, activity_id });
            authorSection.innerHTML = response;
        } catch (err) {
            console.error('Failed to load media author', err);
        }
    }

    async fetchComments(media_id, activity_id) {
        const commentSection = this.container.querySelector('.th-media-comments .comment_section');
        const footerLinks = this.container.querySelector('.th-media-comments .activity-footer-links');

        if (commentSection) commentSection.innerHTML = '<span class="comments_loading"></span>';

        try {
            // NOTE: Using jQuery.ajax behavior via postAction wrapper if it returns parsed JSON
            // Original code expected: response.data.comments, response.data.activity_footer
            const response = await postAction('th_bp_media_comments', { media_id, activity_id });

            if (response && response.data) {
                if (commentSection) commentSection.innerHTML = response.data.comments;
                if (footerLinks) footerLinks.innerHTML = response.data.activity_footer;

                // Sync generic Like button if present in the modal
                this.syncLikeButton(activity_id, response.data.activity_footer);
            }
        } catch (err) {
            console.error('Failed to load media comments', err);
            if (commentSection) commentSection.innerHTML = '<p class="error">Could not load comments.</p>';
        }
    }

    /**
     * Updates the main activity stream like button if the modal like button changes status
     */
    syncLikeButton(activityId, newFooterHtml) {
        // Logic extracted from original to keep UI in sync
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = newFooterHtml;
        const newButton = tempDiv.querySelector('.th-bp-post-like-button');

        if (newButton) {
            const streamButton = document.querySelector(`#activity-${activityId} .th-bp-post-like-button`);
            // Only update if stream button doesn't have a specific comment-id (is main activity like)
            if (streamButton && !streamButton.querySelector('a[data-comment-id]')) {
                streamButton.innerHTML = newButton.innerHTML;
            }
        }
    }
}

/**
 * Public Init Function
 */
export function initMediaViewer() {
    return new MediaViewer();
}